import React from 'react';
import { trackEvent, BOOKING_SUPERVISION_URL, BOOKING_SCREENING_URL, EMAIL_ADDRESS } from '../lib/tracking';
import { optimizeImage } from '../lib/image';

export default function GroupSupervision() {
  return (
    <div className="bg-[#ECEEF1]">
      {/* Hero */}
      <section className="py-16 sm:py-24 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E] block">
                Erhverv · Faglig Sparring
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#5A232E] tracking-tight leading-[1.12]">
                Gruppesupervision for fagpersoner
              </h1>
              <p className="text-base sm:text-lg text-[#6c7480] leading-relaxed">
                Er du psykoterapeut, lærer, pædagog eller arbejder du med mennesker i sårbare positioner? Jeg tilbyder professionel gruppesupervision med fokus på nervesystem, relationel praksis og forebyggelse af omsorgstræthed og udbrændthed.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={BOOKING_SUPERVISION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('cta_booking_click', { location: 'group_supervision_hero' })}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Book supervision</span>
                </a>
                <a
                  href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel på gruppesupervision`}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#ECEEF1] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] hover:bg-[#D9DDE2] transition-colors no-break whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope" aria-hidden="true" />
                  <span>Kontakt for gruppetilbud</span>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-[#D9DDE2]">
                <img
                  src={optimizeImage('kim-outdoor-park-3.webp', 800)}
                  alt="Kim Palasz - Gruppesupervision"
                  className="w-full h-auto object-cover max-h-[440px]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indhold & Rammer */}
      <section className="py-20 sm:py-24 bg-[#ECEEF1] border-b border-[#D9DDE2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs uppercase tracking-widest font-semibold text-[#5A232E]">
                Faglighed & Tryghed
              </span>
              <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
                Hvad får du ud af gruppesupervision?
              </h2>
              <div className="text-sm sm:text-base text-[#11161d] space-y-4 leading-relaxed">
                <p>
                  Supervisionen skaber et trygt rum til refleksion, faglig sparring og forebyggelse af udbrændthed. Gennem neuroaffektiv forståelse og et traumeinformeret blik får du nye perspektiver på din praksis og dine relationer til klienter og borgere.
                </p>
                <p>
                  Vi dykker ned i konkrete cases, udforsker de dynamikker og følelser der opstår i relationen, og arbejder med redskaber, der genopretter ro i eget nervesystem.
                </p>
                <ul className="list-disc list-inside space-y-2 text-sm text-[#6c7480]">
                  <li>Få faglig sparring og nye perspektiver på klienter og arbejdsmetoder</li>
                  <li>Dykke ned i dine cases og få supervision fra en erfaren supervisor</li>
                  <li>Små grupper med 4-6 deltagere for maksimal fordybelse og tryghed</li>
                  <li>Fokus på forebyggelse af sekundær traumatisering og omsorgstræthed</li>
                </ul>
              </div>

              <div className="p-5 rounded-2xl bg-white border border-[#D9DDE2] text-xs sm:text-sm text-[#5A232E] flex flex-wrap gap-4 font-semibold shadow-xs">
                <span>Varighed: 3 timer pr. gang</span>
                <span>•</span>
                <span>Pris: 1.250 kr. pr. deltager</span>
                <span>•</span>
                <span>Sted: Marengovej 2 på Amager eller online</span>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white rounded-3xl p-8 border border-[#D9DDE2] shadow-sm space-y-5">
                <div className="w-12 h-12 rounded-2xl bg-[#5A232E] text-white flex items-center justify-center text-xl">
                  <i className="fa-solid fa-users" aria-hidden="true" />
                </div>
                <h3 className="font-display text-2xl text-[#5A232E]">
                  Trygge rammer i mindre grupper
                </h3>
                <p className="text-xs sm:text-sm text-[#6c7480] leading-relaxed">
                  Gruppesupervision afholdes i faste eller åbne hold med 4-6 deltagere. Det sikrer, at der er god tid til alles cases og refleksioner i et fortroligt fagligt fællesskab.
                </p>
                <div className="pt-2">
                  <a
                    href={BOOKING_SUPERVISION_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs font-semibold text-[#5A232E] hover:underline"
                  >
                    <span>Book din plads via bookingsiden →</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white border-b border-[#D9DDE2]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="font-display text-3xl sm:text-4xl text-[#5A232E]">
            Ønsker du et skræddersyet forløb til dit team?
          </h2>
          <p className="text-sm sm:text-base text-[#6c7480] max-w-xl mx-auto">
            Jeg tilrettelægger også interne supervisionsforløb for institutioner, bosteder, skoler og terapeutiske arbejdspladser.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <a
              href={BOOKING_SUPERVISION_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              <span>Book supervision</span>
            </a>
            <a
              href={`mailto:${EMAIL_ADDRESS}?subject=Forespørgsel på skræddersyet gruppesupervision`}
              className="inline-flex items-center gap-2 px-7 py-4 rounded-xl bg-[#ECEEF1] hover:bg-[#D9DDE2] text-[#5A232E] font-medium text-sm border border-[#D9DDE2] transition-colors no-break whitespace-nowrap"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Indhent uforpligtende tilbud</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
